import useCommonApiErrorHandling from '@/hooks/useCommonApiErrorHandling';
import useInsertServiceIdInApiUrl from '@/hooks/useInsertServiceIdInApiUrl';
import useSetLoading from '@/hooks/useSetLoading';
import { mobileCampaignState } from '@/mobile/states/campaignState';
import { MobileCampaignResponse } from '@/mobile/types/api/mypage/campaign';
import apiMobile from '@/mobile/utils/apiMobile';
import { ApiUrlMobile } from '@/mobile/utils/constants';
import { getSigV4HeadersMobileForGet } from '@/utils/cognito';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';

const apiUrl = ApiUrlMobile.Read.CAMPAIGN;

// キャンペーン一覧は複数画面で参照されるため、取得後にまとめて state へ退避する。
/**
 * キャンペーン情報取得用カスタムフック
 * API呼び出し・ローディング制御・エラーハンドリングを共通化し、
 * 呼び出し元では execute() を呼ぶだけで取得処理が完結する。
 *
 * @returns execute - API取得処理を実行する関数
 */
const useGetCampaign = () => {
  const { startLoading, finishLoading } = useSetLoading();
  const setCampaignInfo = useSetRecoilState(mobileCampaignState);
  const { commonErrorHandling, commonResponseCodeHandling } = useCommonApiErrorHandling();
  const { insertServiceIdInApiUrl } = useInsertServiceIdInApiUrl();

  const execute = useCallback(async () => {
    const apiUrlWithServiceId = insertServiceIdInApiUrl(apiUrl);
    try {
      startLoading(apiUrlWithServiceId);
      const headers = await getSigV4HeadersMobileForGet(apiUrlWithServiceId);
      const { data }: AxiosResponse<MobileCampaignResponse, any> = await apiMobile.get(
        apiUrlWithServiceId,
        headers
      );

      const { isError } = commonResponseCodeHandling({
        responseCode: data.response_code,
        isTransitionTop: true,
      });

      if (isError) return;

      // 表示判定や詳細描画でレスポンス全体を使うため、加工せずそのまま保持する。
      setCampaignInfo(data);
    } catch (error: any) {
      commonErrorHandling({ error, isTransitionTop: true });
    } finally {
      finishLoading(apiUrlWithServiceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute };
};

export default useGetCampaign;
